import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import PlayerMatchupComponent from './PlayerMatchupComponent';
import { PlayerMatchupTypes, PlayerMatchupFormats } from '@mlb/react-patterns/components/PlayerMatchup/playerMatchup.types';
import {
    PlayerMatchupWrapper,
    MatchupWrapper,
    MatchupLabel
} from './PlayerMatchup.style';
import { HeadshotTypes } from '@mlb/react-patterns/components/Headshot/headshot.types';
import useTracking from '../../utils/hooks/useTracking';
import { useTranslator } from '../../utils/hooks/useTranslator';
import { statsMapsDuel } from '../PlayerDuel/utils/statsMapsDuel';
import buildPlayerStatsDuel from '../PlayerDuel/utils/buildPlayerStatsDuel';

const { SPOTS } = HeadshotTypes;
const { PITCHERLIVE, HITTERLIVE } = PlayerMatchupTypes;
const { ORIGINAL } = PlayerMatchupFormats;

const PlayerMatchup = ({
    batter,
    pitcher,
    currentBatSide,
    abstractGameCode,
    gameStateOverride,
    language,
    deviceInteraction
}) => {
    const Tracking = useTracking();

    const onClick = useCallback((e) => {
        const text = e?.target.innerText;
        const playerId = e?.currentTarget?.dataset?.playerid;
        const url = e?.currentTarget?.dataset?.url;

        const activity = 'Player Click';
        const cfg = {
            componentName: ['Live', 'Match Up'],
            text,
            activity,
            linkName: `${activity}: ${playerId}`
        };
        Tracking.track.track_click(cfg);

        if (deviceInteraction.playercard({ title: 'Player Card', url, pid: playerId })) {
            e.preventDefault();
        }
    }, [Tracking, deviceInteraction, language]);

    const gameCodeOverride = gameStateOverride || abstractGameCode;

    let matchups = [];
    const dictMap = {
        hr: useTranslator('labelBrief_h_hr', 'hr'),
        bb: useTranslator('labelBrief_h_bb', 'bb'),
        slg: useTranslator('labelBrief_h_slg', 'slg'),
        ip: useTranslator('labelBrief_ip', 'ip'),
        p: useTranslator('labelBrief_p', 'p'),
        er: useTranslator('labelBrief_era', 'era'),
        pitcherK: useTranslator('labelBrief_p_k', 'k'),
        saves: useTranslator('save_abbrev', 'sv'),
        holds: useTranslator('labelBrief_hld', 'hld'),
        strikeOuts: useTranslator('labelBrief_h_k', 'k'),
        numberOfPitches: useTranslator('labelBrief_p_p', 'p'),
        er: useTranslator('labelBrief_er', 'er'),
        '2b': useTranslator('labelBrief_d', '2b'),
        cs: useTranslator('labelBrief_h_cs', 'cs'),
        runs: useTranslator('labelBrief_h_r', 'r'),
        sb: useTranslator('labelBrief_h_sb', 'sb'),
        hbp: useTranslator('labelBrief_hbp', 'hbp'),
        rbi: useTranslator('labelBrief_rbi', 'rbi'),
        ab: useTranslator('matchup_ab', 'AB'),
        lhp: useTranslator('probables_lhp', 'LHP'),
        rhp: useTranslator('probables_rhp', 'RHP')
    };

    // Only use case right now is for Live games
    if (gameCodeOverride === 'P') {
        return null;
    }

    const desiredHittingStats = {
        before: ['hits'],
        after: ['homeRuns', 'triples', 'doubles', 'baseOnBalls', 'hitByPitch', 'strikeOuts', 'rbi', 'runs', 'stolenBases', 'caughtStealing']
    };
    const desiredPitchingStats = {
        before: [],
        after: ['ip', 'numberOfPitches', 'pitcherK', 'earnedRuns']
    };

    matchups = [pitcher, batter].map((player = {}, i) => {
        const isPitcher = i === 0;
        const { person: { id, boxscoreName, pitchHand = {}, batSide = {} } = {}, stats, person: { nameSlug } = {} } = player;
        const statsConfig = isPitcher ? desiredPitchingStats : desiredHittingStats;
        const statsMap = isPitcher ? statsMapsDuel.pitchersStatsMap : statsMapsDuel.hittersStatsMap;
        const playerStats = buildPlayerStatsDuel(statsConfig, statsMap, stats, dictMap, isPitcher);
        const handPitchCode = pitchHand?.code ? `${pitchHand.code}HP`.toLowerCase() : '';
        const MatchupLabelElement = <MatchupLabel>{isPitcher ? dictMap.p : dictMap.ab}</MatchupLabel>;
        // If we have the current bat side, use it. If not, use the hitter's default code.
        // This should show correct side for switch hitters.
        const batSideCode = currentBatSide?.code || batSide.code;
        return (
            <PlayerMatchupComponent
                type={ isPitcher ? PITCHERLIVE : HITTERLIVE }
                playerId={ id }
                key={ i }
                stacked={ false }
                format={ ORIGINAL }
                headshotWidth={ '32px' }
                customPlayerInfo={ isPitcher ? `${dictMap?.[handPitchCode]}` : batSideCode }
                linkProps={{
                    handlers: { onClick }
                }}
                showHeadShot={ true }
                playerStats={ playerStats }
                name={ boxscoreName }
                headshotZoomScale={ 1 }
                headshotType={ SPOTS }
                prefix={ MatchupLabelElement }
                headshotRatio='1:1'
                language={ language }
            />
        );
    });

    return (
        <PlayerMatchupWrapper>
            { matchups.length && (
                <MatchupWrapper>
                    { matchups[0] }
                    { matchups[1] }
                </MatchupWrapper>
            )}
        </PlayerMatchupWrapper>
    );
};

PlayerMatchup.propTypes = {
    batter: PropTypes.object.isRequired,
    pitcher: PropTypes.object.isRequired,
    currentBatSide: PropTypes.object,
    abstractGameCode: PropTypes.string.isRequired,
    gameStateOverride: PropTypes.string,
    language: PropTypes.string.isRequired,
    deviceInteraction: PropTypes.object
};

export default PlayerMatchup;
